import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "../lib/api";
import { Page } from "../components/AppShell";
import { Receipt, CreditCard, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [params] = useSearchParams();
  const paidFlash = params.get("paid");
  const canceledFlash = params.get("canceled");

  const load = () =>
    api.get("/customer/invoices")
      .then((r) => setInvoices(r.data || []))
      .catch(() => { /* core admin will 403 — silent for empty-state */ })
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    setBusyId(id); setErr("");
    try {
      const r = await api.post(`/customer/invoices/${id}/checkout`, {});
      window.location.href = r.data.checkout_url;
    } catch (e) { setErr(formatErr(e)); setBusyId(null); }
  };

  return (
    <Page
      title="My Invoices"
      subtitle="INVOICES YOUR HOST ORGS HAVE SENT YOU"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "My Invoices"]}
      testid="my-invoices-page"
    >
      <div className="max-w-6xl mx-auto">

      {paidFlash && (
        <div className="surface px-3 py-2 mb-3 text-sm text-green-700 border-green-300 bg-green-50 flex items-center gap-2" data-testid="myinv-paid-flash">
          <CheckCircle size={14} /> Payment received — thank you!
        </div>
      )}
      {canceledFlash && (
        <div className="surface px-3 py-2 mb-3 text-sm text-amber-800 border-amber-300 bg-amber-50 flex items-center gap-2" data-testid="myinv-canceled-flash">
          <XCircle size={14} /> Checkout canceled. You can try again any time.
        </div>
      )}
      {err && <div className="text-xs text-destructive mb-2" data-testid="myinv-error">{err}</div>}

      {loading ? <div className="p-8 text-center text-muted-foreground" data-testid="myinv-loading">Loading…</div>
       : invoices.length === 0 ? (
        <div className="surface p-12 text-center text-muted-foreground" data-testid="myinv-empty">
          <Receipt size={28} weight="thin" className="mx-auto mb-2" /> No invoices yet.
        </div>
       ) : (
        <div className="surface">
          <table className="dense">
            <thead><tr><th>INVOICE #</th><th>HOST ORG</th><th>TOTAL</th><th>BALANCE</th><th>ISSUED</th><th>DUE</th><th>STATUS</th><th></th></tr></thead>
            <tbody>
              {invoices.map((i) => {
                const open = i.status !== "paid" && Number(i.balance) > 0;
                return (
                  <tr key={i.id} data-testid={`myinv-row-${i.id}`}>
                    <td className="mono text-xs">{i.invoice_number}</td>
                    <td className="text-xs">{i.host_org_name}</td>
                    <td className="mono text-xs">{fmtMoney(i.total)}</td>
                    <td className="mono text-xs">{fmtMoney(i.balance)}</td>
                    <td className="text-xs text-muted-foreground mono">{fmtDate(i.issue_date)}</td>
                    <td className="text-xs text-muted-foreground mono">{fmtDate(i.due_date)}</td>
                    <td className="text-xs uppercase mono">{i.status || "—"}</td>
                    <td className="text-right">
                      {open ? (
                        <button onClick={() => pay(i.id)} disabled={busyId === i.id}
                          className="btn btn-primary btn-sm" data-testid={`myinv-pay-${i.id}`}>
                          <CreditCard size={12} /> {busyId === i.id ? "OPENING…" : "PAY"}
                        </button>
                      ) : <span className="badge badge-default" style={{ background: "#D1FAE5", color: "#065F46" }}>PAID</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
       )}
      </div>
    </Page>
  );
}
